import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  LockKey, LockKeyOpen, Plus, Trash, ArrowLeft, Warning, NotePencil, Key,
} from '@phosphor-icons/react';
import { useVault } from '../contexts/VaultContext';
import { useAuth } from '../contexts/AuthContext';
import { Frame } from '../components/shell/AppShell';
import PageHeader from '../components/shell/PageHeader';
import { isPrimary, moreFor } from '../lib/navigation';
import vaultArt from '../assets/bg/private-vault.webp';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const Vault = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    status, scheme, notes, busy, supported,
    refreshStatus, createVault, unlock, lock, saveNote, deleteNote, resetVault,
    upgradeVault, changePassphrase, recoverWithKey,
  } = useVault();

  const [pass, setPass] = useState('');
  const [pass2, setPass2] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // {id?, title, body}
  const [recoveryKey, setRecoveryKey] = useState(null); // shown once
  const [mode, setMode] = useState(null); // null | 'recover' | 'changepass'
  const [recoveryInput, setRecoveryInput] = useState('');

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (pass.length < 10) { setError('Use at least 10 characters'); return; }
    if (pass !== pass2) { setError('The two passphrases do not match'); return; }
    const r = await createVault(pass);
    if (r.ok) {
      setPass(''); setPass2('');
      setRecoveryKey(r.recoveryKey);   // shown once, must be written down
      toast.success('Vault created');
    } else setError(r.error);
  };

  const handleUpgrade = async (e) => {
    e.preventDefault();
    setError('');
    const r = await upgradeVault(pass);
    if (r.ok) { setPass(''); setRecoveryKey(r.recoveryKey); toast.success('Vault upgraded'); }
    else setError(r.error);
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    setError('');
    const r = await recoverWithKey(recoveryInput);
    if (r.ok) { setRecoveryInput(''); setMode('changepass'); toast.success('Recovered — now set a new passphrase'); }
    else setError(r.error);
  };

  const handleChangePass = async (e) => {
    e.preventDefault();
    setError('');
    if (pass.length < 10) { setError('Use at least 10 characters'); return; }
    if (pass !== pass2) { setError('The two passphrases do not match'); return; }
    const r = await changePassphrase(pass);
    if (r.ok) { setPass(''); setPass2(''); setMode(null); toast.success('Passphrase changed'); }
    else setError(r.error);
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    setError('');
    const r = await unlock(pass);
    if (r.ok) setPass('');
    else setError(r.error);
  };

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!editing?.title?.trim() && !editing?.body?.trim()) return;
    setSaving(true);
    try {
      const r = await saveNote({
        id: editing.id,
        title: editing.title || 'Untitled',
        body: editing.body || '',
      });
      if (!r.ok) throw new Error(r.error || 'save failed');
      setEditing(null);
      toast.success('Saved');
    } catch (e) {
      // Never fail silently — the note stays open so nothing is lost.
      toast.error(e.response?.data?.detail || 'Could not save. Your note is still here — try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await deleteNote(id);
      setEditing(null);
      toast('Note deleted');
    } catch (e) {
      toast.error('Could not delete the note. Try again.');
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the vault? EVERY note is permanently destroyed. Only do this if you have forgotten your passphrase.')) return;
    await resetVault();
    toast('Vault reset');
  };

  // A primary destination for members, so no Back; for staff it is their
  // own vault under More's Society group, and Back returns there.
  const Header = (
    <PageHeader
      title="Private Vault"
      subtitle="Encrypted private notes"
      onBack={isPrimary(user, 'vault') ? undefined : () => navigate(moreFor(user))}
      actions={status === 'unlocked' && (
        <Button variant="outline" size="sm" onClick={lock} data-testid="vault-lock">
          <LockKey className="w-4 h-4 mr-1.5" /> Lock
        </Button>
      )}
    >
      <p className="mt-1 text-xs text-muted-foreground">Encrypted on your device — only you can read it</p>
    </PageHeader>
  );

  if (!supported) {
    return (
      <Frame user={user} background={vaultArt} activeKey={isPrimary(user, 'vault') ? 'vault' : 'more'}>
        {Header}
        <div className="max-w-md mx-auto p-6 text-center text-sm text-muted-foreground">
          Your browser cannot use the vault. It requires a modern browser over HTTPS.
        </div>
      </Frame>
    );
  }

  return (
    <Frame user={user} background={vaultArt} activeKey={isPrimary(user, 'vault') ? 'vault' : 'more'}>
    <div data-testid="vault-page">
      {Header}
      <div className="max-w-2xl mx-auto p-4 md:p-6">

        {/* Recovery key — shown exactly once, after creation or upgrade */}
        {recoveryKey && (
          <div className="border rounded-xl bg-card/85 backdrop-blur-sm p-6 space-y-4 mb-4" style={{ borderColor: 'hsl(var(--gold))' }} data-testid="vault-recovery-key">
            <div className="text-center">
              <Key className="w-8 h-8 mx-auto mb-2" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
              <h2 className="font-heading text-xl">Write this down now</h2>
              <p className="text-sm text-muted-foreground mt-1">
                This is your recovery key. It is the <strong>only</strong> way back into your vault
                if you forget your passphrase. It will never be shown again.
              </p>
            </div>
            <p className="font-mono text-center text-lg tracking-wider bg-muted/60 rounded-lg p-4 border border-border select-all" data-testid="recovery-key-value">
              {recoveryKey}
            </p>
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Warning className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Store it somewhere separate from your passphrase — on paper, or in a password manager. Anyone holding it can open your vault.</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1"
                onClick={() => { navigator.clipboard.writeText(recoveryKey).then(() => toast.success('Copied')).catch(() => {}); }}>
                Copy
              </Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90"
                onClick={() => setRecoveryKey(null)} data-testid="recovery-key-done">
                I've saved it
              </Button>
            </div>
          </div>
        )}

        {/* Legacy vault → offer the upgrade that enables recovery */}
        {status === 'unlocked' && scheme === 1 && !recoveryKey && (
          <form onSubmit={handleUpgrade} className="border rounded-xl bg-card/85 backdrop-blur-sm p-4 mb-4 space-y-3" style={{ borderColor: 'hsl(var(--gold))' }} data-testid="vault-upgrade">
            <p className="text-sm font-medium">Enable passphrase recovery</p>
            <p className="text-xs text-muted-foreground">
              This vault was made before recovery keys existed. Confirm your passphrase and we'll
              re-encrypt your notes and give you a recovery key. Your notes stay exactly as they are.
            </p>
            <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)}
              placeholder="Your current passphrase" aria-label="Your current passphrase" required className="h-11" data-testid="vault-upgrade-pass" />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="vault-upgrade-submit">
              {busy ? 'Upgrading…' : 'Upgrade vault'}
            </Button>
          </form>
        )}

        {/* Change passphrase */}
        {status === 'unlocked' && mode === 'changepass' && (
          <form onSubmit={handleChangePass} className="border border-border rounded-xl bg-card/85 backdrop-blur-sm p-5 mb-4 space-y-3" data-testid="vault-changepass">
            <p className="font-heading text-lg">Set a new passphrase</p>
            <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)}
              placeholder="New passphrase (min 10 characters)" aria-label="New passphrase" required autoFocus className="h-11" />
            <Input type="password" value={pass2} onChange={(e) => setPass2(e.target.value)}
              placeholder="Repeat new passphrase" aria-label="Repeat new passphrase" required className="h-11" />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <p className="text-xs text-muted-foreground">Your existing recovery key keeps working.</p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1"
                onClick={() => { setMode(null); setPass(''); setPass2(''); setError(''); }}>Cancel</Button>
              <Button type="submit" disabled={busy} className="flex-1 bg-primary hover:bg-primary/90">
                {busy ? 'Saving…' : 'Change passphrase'}
              </Button>
            </div>
          </form>
        )}

        {status === 'loading' && (
          <p className="text-sm text-muted-foreground text-center py-10">Loading…</p>
        )}

        {status === 'uninitialized' && (
          <form onSubmit={handleCreate} className="space-y-4 border border-border rounded-xl bg-card/85 backdrop-blur-sm p-6" data-testid="vault-setup">
            <div className="text-center">
              <LockKey className="w-8 h-8 mx-auto mb-2" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
              <h2 className="font-heading text-xl">Create your vault</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Choose a passphrase. It never leaves this device and is not stored anywhere.
              </p>
            </div>
            <div className="flex items-start gap-2 text-xs rounded-lg border border-destructive/30 bg-destructive/10 p-3">
              <Warning className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />
              <span>
                You will be given a recovery key next. If you lose <strong>both</strong> this passphrase and
                that key, your notes are <strong>gone forever</strong> — nobody can recover them, not even the
                Founder. Do not store your Arcanum password or 2FA recovery codes here.
              </span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="p1">Passphrase</Label>
              <Input id="p1" type="password" value={pass} onChange={(e) => setPass(e.target.value)}
                placeholder="At least 10 characters" required autoFocus data-testid="vault-pass" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="p2">Repeat passphrase</Label>
              <Input id="p2" type="password" value={pass2} onChange={(e) => setPass2(e.target.value)} required data-testid="vault-pass2" />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="vault-create">
              {busy ? 'Creating…' : 'Create vault'}
            </Button>
          </form>
        )}

        {status === 'locked' && mode === 'recover' && (
          <form onSubmit={handleRecover} className="space-y-4 border border-border rounded-xl bg-card/85 backdrop-blur-sm p-6" data-testid="vault-recover">
            <div className="text-center">
              <Key className="w-8 h-8 mx-auto mb-2" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
              <h2 className="font-heading text-xl">Use your recovery key</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Enter the recovery key you wrote down when the vault was created.
              </p>
            </div>
            <Input value={recoveryInput} onChange={(e) => setRecoveryInput(e.target.value)}
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX" aria-label="Recovery key" required autoFocus
              className="font-mono text-center tracking-wider" data-testid="vault-recovery-input" />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="vault-recover-submit">
              {busy ? 'Checking…' : 'Recover vault'}
            </Button>
            <button type="button" onClick={() => { setMode(null); setError(''); setRecoveryInput(''); }}
              className="w-full text-xs text-muted-foreground hover:text-foreground">
              Back
            </button>
          </form>
        )}

        {status === 'locked' && mode !== 'recover' && (
          <form onSubmit={handleUnlock} className="space-y-4 border border-border rounded-xl bg-card/85 backdrop-blur-sm p-6" data-testid="vault-unlock">
            <div className="text-center">
              <LockKey className="w-8 h-8 mx-auto mb-2" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
              <h2 className="font-heading text-xl">Vault locked</h2>
              <p className="text-sm text-muted-foreground mt-1">Enter your passphrase to unlock.</p>
            </div>
            <Input type="password" value={pass} onChange={(e) => setPass(e.target.value)}
              placeholder="Passphrase" aria-label="Passphrase" required autoFocus className="h-11" data-testid="vault-unlock-pass" />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-primary hover:bg-primary/90" data-testid="vault-unlock-submit">
              {busy ? 'Unlocking…' : 'Unlock'}
            </Button>
            <button type="button" onClick={() => { setMode('recover'); setError(''); }}
              className="w-full text-xs hover:underline" style={{ color: 'hsl(var(--gold))' }}
              data-testid="vault-use-recovery">
              Forgotten your passphrase? Use your recovery key
            </button>
            <button type="button" onClick={handleReset} className="w-full text-xs text-muted-foreground hover:text-destructive">
              No recovery key either? Reset the vault (destroys all notes)
            </button>
          </form>
        )}

        {status === 'unlocked' && (
          <div className="space-y-4" data-testid="vault-notes">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LockKeyOpen className="w-5 h-5" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
                <h2 className="font-heading text-lg">{notes.length} note{notes.length === 1 ? '' : 's'}</h2>
              </div>
              <div className="flex items-center gap-2">
                {scheme === 2 && mode !== 'changepass' && (
                  <Button size="sm" variant="outline" onClick={() => { setMode('changepass'); setPass(''); setPass2(''); setError(''); }}
                    data-testid="vault-change-pass">
                    <Key className="w-4 h-4 mr-1.5" /> Passphrase
                  </Button>
                )}
                <Button size="sm" className="bg-primary hover:bg-primary/90"
                  onClick={() => setEditing({ title: '', body: '' })} data-testid="vault-new-note">
                  <Plus className="w-4 h-4 mr-1.5" /> New note
                </Button>
              </div>
            </div>
            <hr className="gold-rule" />

            {notes.length === 0 && (
              <div className="text-center py-12">
                <NotePencil className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
                <p className="text-sm text-muted-foreground">Nothing here yet. Your notes stay encrypted on the server.</p>
              </div>
            )}

            <div className="grid gap-2">
              {notes.map((n) => (
                <button
                  key={n.id}
                  onClick={() => setEditing({ id: n.id, title: n.title, body: n.body })}
                  // min-w-0 so the grid cell may shrink below its content:
                  // without it a long note line makes this button wider than
                  // the screen, `truncate` never gets to clip, and the whole
                  // page scrolls sideways.
                  className="text-left min-w-0 border border-border rounded-lg bg-card/85 backdrop-blur-sm p-3 hover:border-secondary/50 transition-colors"
                  data-testid={`vault-note-${n.id}`}
                >
                  <p className="font-medium text-sm truncate">{n.title || 'Untitled'}</p>
                  <p className="text-xs text-muted-foreground truncate mt-0.5">
                    {n.broken ? 'Could not decrypt with this passphrase' : (n.body || 'Empty')}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit note' : 'New note'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Title"
              aria-label="Title"
              value={editing?.title || ''}
              onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))}
              autoFocus
              data-testid="vault-note-title"
            />
            <Textarea
              placeholder="Write anything you want kept private."
              aria-label="Note"
              value={editing?.body || ''}
              onChange={(e) => setEditing((s) => ({ ...s, body: e.target.value }))}
              className="min-h-[200px]"
              data-testid="vault-note-body"
            />
            <div className="flex gap-2">
              {editing?.id && (
                <Button variant="outline" className="text-destructive" onClick={() => handleDelete(editing.id)} data-testid="vault-note-delete">
                  <Trash className="w-4 h-4" />
                </Button>
              )}
              <Button variant="outline" className="flex-1" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={handleSave} disabled={saving} data-testid="vault-note-save">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </Frame>
  );
};

export default Vault;
